import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Collapse, Toast, SearchBar, Cascader } from "antd-mobile";
import { motion } from "framer-motion";
import {
  UnorderedListOutline,
  PayCircleOutline,
  SetOutline,
} from "antd-mobile-icons";
import { useNavigate } from "react-router-dom";
import modules from "src/common/utils/modules";
import apiRequest from "src/common/utils/apiRequest";
import { LoadingOutlined, ThunderboltOutlined } from "@ant-design/icons";

import { Button, Col, Row } from "antd";
import MobilePanel from "src/ai/MobilePanel";

import AIIMG from "src/assets/img/ai.gif";

const MobileNavbar = ({
  drawerVisible,
  setDrawerVisible,
}: {
  setDrawerVisible: (prev: boolean) => void;
  drawerVisible: boolean;
}) => {
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [filteredMenu, setFilteredMenu] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAIWidjetHeight, setShowAIWidjetHeight] = useState<boolean>(false);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [activeNav, setActiveNav] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string>("");

  const navigate = useNavigate();
  const location = useLocation();

  const renderMobileIcon = (iconName: string) => {
    switch (iconName) {
      case "UnorderedListOutline":
        return <UnorderedListOutline />;
      case "PayCircleOutline":
        return <PayCircleOutline />;
      case "SetOutline":
        return <SetOutline />;
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchMenu = async () => {
      setLoadingMenu(true);
      const updatedModules = await Promise.all(
        modules.map(async (route) => {
          try {
            const module = await import(`src/modules/${route.key}/routes`);
            const children = module.default.filter((r: any) => r.navigation);
            return {
              ...route,
              ...(children.length > 0 ? { children } : {}),
            };
          } catch {
            return route;
          }
        })
      );

      const cleanModules = updatedModules.map(({ children, ...mod }) => ({
        ...mod,
        ...(children?.length
          ? {
              children: children.map(({ element, ...c }: any) => c),
            }
          : {}),
      }));

      const API = new apiRequest("initialize/menu", true, true);
      await API.sendAndCache(
        "menu_cache_mobile",
        60000,
        { menu: cleanModules },
        (res: { status: string; data: any[] }) => {
          if (res.status === "success") {
            setMenuItems(res.data || []);
          } else {
            Toast.show({ content: "Failed to load menu", position: "bottom" });
          }
        }
      );

      setLoadingMenu(false);
    };

    fetchMenu();
  }, []);

  const handleClick = (key: string) => {
    navigate(`/${key}`);
    setDrawerVisible(false);
  };

  useEffect(() => {
    setFilteredMenu(
      menuItems
        .map((item) => {
          const children = item.children?.filter((child: any) =>
            child.label.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (
            children?.length > 0 ||
            item.label.toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            return {
              ...item,
              children: children || [],
            };
          }
          return null;
        })
        .filter(Boolean)
    );
  }, [searchTerm, menuItems]);

  useEffect(() => {
    const segments = location.pathname.replace(/^\/+/, "").split("/");

    if (segments[0] === "modules" && segments.length > 1) {
      setActiveNav(location.pathname.replace(/^\/+/, ""));

      setOpenKey(segments[1]);
    }
  }, [location.pathname]);

  return (
    <div style={{ padding: "16px 12px 60px" }}>
      <Row>
        <Col xs={22} sm={22} md={22} lg={24} xl={24}>
          <SearchBar
            placeholder="Search "
            value={searchTerm}
            onChange={setSearchTerm}
            style={{ marginBottom: 12, width: "93%" }}
          />
        </Col>
        <Col xs={2} sm={2} md={2} lg={24} xl={24}>
          <Button
            style={{ float: "right", border: "none" }}
            onClick={() => setShowAIWidjetHeight(!showAIWidjetHeight)}
          >
            <img src={AIIMG} alt="AI" width={20} />
          </Button>
        </Col>
      </Row>

      {loadingMenu ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      ) : (
        <Collapse defaultActiveKey={openKey} accordion>
          {filteredMenu.map((item: any) => (
            <Collapse.Panel key={item.key} title={item.label}>
              {item.children && item.children.length > 0 ? (
                <div style={{ paddingLeft: 12 }}>
                  {item.children.map((child: any) => (
                    <motion.div
                      whileHover={{ scale: 1.01, fontWeight: "bold" }}
                      whileTap={{ scale: 0.99, fontWeight: "bold" }}
                      transition={{ type: "spring", stiffness: 300 }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        key={child.key}
                        onClick={() => handleClick(child.key)}
                        style={
                          activeNav === child.key
                            ? {
                                display: "flex",
                                fontSize: 14,
                                alignItems: "center",
                                padding: "8px 0",
                                cursor: "pointer",
                                fontWeight: "bold",
                                textDecoration: "underline",
                              }
                            : {
                                display: "flex",
                                fontSize: 14,
                                alignItems: "center",
                                padding: "8px 0",
                                cursor: "pointer",
                              }
                        }
                      >
                        <span style={{ marginRight: 8 }}>
                          {renderMobileIcon(child.icon)}
                        </span>
                        <span>{child.label}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div
                  onClick={() => handleClick(item.key)}
                  style={{
                    padding: "8px 0",
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                    paddingLeft: 12,
                  }}
                >
                  {renderMobileIcon(item.icon)} {item.label}
                </div>
              )}
            </Collapse.Panel>
          ))}
        </Collapse>
      )}
      <>{showAIWidjetHeight && <MobilePanel height={500} />}</>
    </div>
  );
};

export default MobileNavbar;
